import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Card,
    CardActions,
    CardMedia,
    Button,
    Typography,
    Avatar,
    IconButton,
} from '@mui/material';
import {
    Publish,
    Videocam,
    FeaturedVideoOutlined,
    SportsBasketball,
    VpnKey,
    CardGiftcard,
    Gavel,
    ChildCare,
    PersonOutline,
    ContentCopy,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { getDateLocale } from '../../../lib/dateLocale';
import {
    AppContext,
    SttFormText,
    SttFormDate,
    SttFormTextarea,
    SttButtonSave,
    SttButtonDelete,
    SttSelectGender,
    SttFormSwitch,
    SttSelectUser,
    SttFormSelect,
    SttSelectCity,
    SttSportChips,
    SttUnlockerChips,
    UploadFileDialog,
    SttButton,
} from '../../../sporttia/all';
import { getErrorMessage } from '../../../lib/utils';
import SttLinkList from './SttLinkList';
import MshipBoletos from './MshipBoletos';
import { SttSelectConsentList } from '../../../sporttia/lists/SttSelectConsentList';
import BoletoDialog from '../../../components/dialogs/BoletoDialog';
import { getPath } from '../../Pages';
import SttWebcamCaptureDialog from './SttWebcamCaptureDialog';
import MshipPupils from './MshipPupils';
import { useInteractionsFiles } from '../../../lib/hooks';
import useMshipsService from '../../../services/MshipsService';
import type { Sport as SportModel } from '../../../types/models/Sport';
import type { User as UserModel } from '../../../types/models/User';
import { PERMISSIONS } from '../../../lib/permissions';

type Unlocker = {
    id: number;
    [key: string]: any;
};

type Consent = {
    template: {
        id: number;
        name: string;
        text?: string;
        textShort?: string;
    };
    file?: {
        id: number;
    };
};

type Boleto = {
    id?: number;
    user?: UserModel;
    bono?: {
        id: number;
        name: string;
        price: number;
    };
    payment?: {
        id: number;
        price: number;
        paymentForm: string;
    };
    price?: number;
    expiration?: string;
    notes?: string;
    consumed?: number;
    amount?: number;
    trash?: boolean;
};

type MshipData = {
    id?: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    identificationType?: string;
    dni?: string;
    gender?: string;
    notes?: string;
    birthday?: string;
    email?: string;
    email2?: string;
    mobile?: string;
    mobile2?: string;
    address?: string;
    addrVia?: string;
    addrType?: string;
    addrNumber?: string;
    addrLetter?: string;
    iban?: string | null;
    postalCode?: string;
    certified?: boolean | null;
    status?: string;
    img?: { url: string };
    user?: {
        id: number;
        login?: string;
        lastVisit?: string;
        sports?: SportModel[];
    };
    tutor?: {
        id?: number;
        mship?: { fullName?: string };
        fullName?: string;
    };
    city?: {
        id?: number;
        name?: string;
    };
    unlockers?: Unlocker[];
    sc?: {
        id: number;
    };
};

type PupilData = {
    id: number;
    name?: string;
    surname1?: string;
    surname2?: string;
};

type Props = {
    mship: MshipData;
    onChange: (params: { name: string | undefined; value: unknown }) => void;
    setMship: (mship: MshipData) => void;
    onClose: () => void;
};

type UploadResponse = {
    file: { url: string };
};

export default function MshipDetail({
    mship,
    onChange,
    setMship,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const [boletos, setBoletos] = useState<Boleto[]>([]);
    const [isCertified, setIsCertified] = useState<boolean>(
        mship.certified !== null && mship.certified !== undefined,
    );
    const [consents, setConsents] = useState<Consent[]>([]);
    const [openUploadPhoto, setOpenUploadPhoto] = useState<boolean>(false);
    const [openedBoleto, setOpenedBoleto] = useState<Boleto | null>();
    const [openWebcamCapture, setOpenWebcamCapture] = useState<boolean>(false);
    const [pupils, setPupils] = useState<PupilData[]>([]);
    const [validateForm, setValidateForm] = useState<boolean>(false);
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();

    /**
     * Mship changes
     */
    useEffect(() => {
        if (mship.id) {
            loadBoletos();

            loadConsents();

            loadPupils();

            setIsCertified(
                mship.certified !== null && mship.certified !== undefined,
            );
        }
    }, [mship.id]);

    const mshipsService = useMshipsService();

    const updateMshipMutation = mshipsService.useUpdateMship({
        onSuccess: () => {
            cxt?.showMessage('S', t('generic.saved'));
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    const deleteMshipMutation = mshipsService.useDeleteMship({
        onSuccess: () => {
            navigate(getPath('mships'));
            cxt?.showMessage('S', t('generic.deleted'));
            onClose();
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    const handleOnSave = () => {
        const allParams: Record<string, string | null | undefined> = {
            name: mship.name,
            surname1: mship.surname1,
            surname2: mship.surname2,
            identificationType: mship.identificationType,
            dni: mship.dni,
            gender: mship.gender,
            notes: mship.notes,
            birthday: mship.birthday,
            email: mship.email,
            email2: mship.email2,
            mobile: mship.mobile,
            mobile2: mship.mobile2,
            address: mship.address,
            addrVia: mship.addrVia,
            addrType: mship.addrType,
            addrNumber: mship.addrNumber,
            addrLetter: mship.addrLetter,
            iban: mship.iban,
            postalCode: mship.postalCode,
            idTutor: mship.tutor?.id ? String(mship.tutor.id) : null,
            idCity: mship.city?.id ? String(mship.city.id) : undefined,
        };

        // Filter out only undefined values - allow empty strings and null to clear fields
        const params = Object.fromEntries(
            Object.entries(allParams).filter(
                ([, value]) => value !== undefined,
            ),
        );

        updateMshipMutation.mutate({
            id: mship.id as number,
            params: params as any,
        });
    };

    const handleOnRemove = () => {
        deleteMshipMutation.mutate({
            id: mship.id as number,
            params: {
                confirmation: true,
            },
        });
    };

    const handleForm = (p: { name: string | undefined; value: unknown }) => {
        onChange(p);
    };

    const loadBoletos = () => {
        cxt?.api('GET', `/mships/${mship.id}/boletos`, {
            params: { rows: 1000 },
            success: (r: { rows: Boleto[] }) => {
                setBoletos(r.rows);
            },
        });
    };

    const loadConsents = () => {
        cxt?.api('GET', `/mships/${mship.id}/consents`, {
            params: { rows: 1000 },
            success: (r: { rows: Consent[] }) => {
                setConsents(r.rows);
            },
        });
    };

    const loadPupils = () => {
        cxt?.api('GET', `/mships/${mship.id}/pupils`, {
            params: { rows: 1000 },
            success: (r: { rows: PupilData[] }) => {
                setPupils(r.rows);
            },
        });
    };

    const toggleCertification = () => {
        cxt?.api('PUT', `/mship/${mship.id}/certifications/toggle`, {
            success: () => {
                setIsCertified(!isCertified);
                cxt?.showMessage('S', t('generic.saved'));
            },
        });
    };

    function printUserCard() {
        /* openUrl( <----- esto no funciona ya con los requisitos de seguridad más estrictos que hay en v7
            `${cxt.getResource(
                `/mships/${mship.id}/card.pdf`,
            )}?width=88&height=55`,
        ); */

        downloadFile(
            `${cxt?.getResource(
                `/mships/${mship.id}/card.pdf`,
            )}?width=88&height=55`,
            'application/pdf',
            {},
            'card.pdf',
        );
    }

    return (
        <>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Card>
                        {mship.img ? (
                            <CardMedia
                                component="img"
                                image={mship.img.url}
                                alt={mship.name}
                                sx={{
                                    aspectRatio: '1',
                                    objectFit: 'cover',
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    aspectRatio: '1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'grey.100',
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        backgroundColor: 'grey.300',
                                    }}
                                >
                                    {/* `grey.500` sobre `grey.300` daba 2,03:1
                                        y la silueta apenas se distinguía del
                                        círculo. Con el gris de texto son
                                        4,4:1. */}
                                    <PersonOutline
                                        sx={{
                                            fontSize: 80,
                                            color: 'text.secondary',
                                        }}
                                    />
                                </Avatar>
                            </Box>
                        )}
                        <CardActions
                            sx={{ justifyContent: 'center', gap: 1, p: 1.5 }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Videocam />}
                                onClick={() => setOpenWebcamCapture(true)}
                            >
                                {t('mship.webcamCapture')}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Publish />}
                                onClick={() => setOpenUploadPhoto(true)}
                            >
                                {t('generic.upload')}
                            </Button>
                        </CardActions>

                        <Box className="flex items-center gap-1 px-3 py-1.5">
                            <Typography
                                className="font-extrabold"
                                variant="body2"
                            >
                                ID:
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                                {mship.id}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    navigator.clipboard.writeText(
                                        mship.id?.toString() ?? '',
                                    );

                                    cxt.showMessage(
                                        'S',
                                        t('generic.copiedToClipboard'),
                                    );
                                }}
                            >
                                <ContentCopy
                                    color="primary"
                                    className="w-4 h-4"
                                />
                            </IconButton>
                        </Box>

                        {mship?.user?.lastVisit && (
                            <Box p={1.5}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t('generic.lastVisit')}
                                </Typography>
                                <Typography variant="body2">
                                    {format(
                                        parseISO(mship.user.lastVisit),
                                        'dd/MM/yyyy HH:mm',
                                        { locale: getDateLocale() },
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </Card>

                    <Box mt={3} flex={1}>
                        <SttButton
                            fullWidth
                            caption={t('mship.printCard')}
                            color="primary"
                            icon={<FeaturedVideoOutlined />}
                            onClick={printUserCard}
                        />
                    </Box>

                    {/* --- Certificated --- */}
                    <Box mt={3}>
                        <SttFormSwitch
                            caption={t('generic.certified')}
                            checked={isCertified}
                            onChange={toggleCertification}
                        />
                    </Box>

                    {/* --- Links --- */}
                    {mship?.user && (
                        <SttLinkList
                            items={[
                                {
                                    icon: <SportsBasketball fontSize="small" />,
                                    caption: `${
                                        mship.user.sports
                                            ? mship.user.sports.length
                                            : 0
                                    } ${t('generic.sports')}`,
                                    content: (
                                        <SttSportChips
                                            sports={mship.user.sports as any}
                                        />
                                    ),
                                },
                                // Hide KIS (Unlockers) when Centro Zero is active
                                ...(!cxt.zero
                                    ? [
                                          {
                                              icon: <VpnKey fontSize="small" />,
                                              caption: `${
                                                  mship.unlockers
                                                      ? mship.unlockers.length
                                                      : 0
                                              } ${t('generic.unlockers')}`,
                                              content: (
                                                  <SttUnlockerChips
                                                      unlockers={
                                                          mship.unlockers
                                                      }
                                                      showCreatingButton
                                                      idUser={mship.user.id}
                                                      idSC={mship.sc?.id}
                                                  />
                                              ),
                                          },
                                      ]
                                    : []),
                                {
                                    icon: <CardGiftcard fontSize="small" />,
                                    caption: `${boletos.length} ${t('generic.bonos')}`,
                                    content: (
                                        <MshipBoletos
                                            boletos={boletos}
                                            onSelect={setOpenedBoleto}
                                        />
                                    ),
                                },
                                // Hide Consents when Centro Zero is active
                                ...(!cxt.zero
                                    ? [
                                          {
                                              icon: <Gavel fontSize="small" />,
                                              caption: `${consents.length} ${t('generic.consents')}`,
                                              content: (
                                                  <SttSelectConsentList
                                                      user={mship.user}
                                                      consents={consents}
                                                      onSign={loadConsents}
                                                  />
                                              ),
                                          },
                                      ]
                                    : []),
                                {
                                    icon: <ChildCare fontSize="small" />,
                                    caption: `${pupils.length} ${t('generic.pupils')}`,
                                    content: (
                                        <MshipPupils
                                            mshipId={mship.id as number}
                                        />
                                    ),
                                },
                            ]}
                        />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 9 }}>
                    <Grid container spacing={3}>
                        {/* --- Name --- */}
                        <SttFormText
                            grid
                            md={12}
                            sm={12}
                            xs={12}
                            name="name"
                            caption={t('generic.name')}
                            defVal={mship.name || ''}
                            helperText={`Login: ${
                                mship.user && mship.user.login
                            }`}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={6}
                            sm={6}
                            xs={12}
                            name="surname1"
                            caption={t('generic.surname1')}
                            defVal={mship.surname1 || ''}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={6}
                            sm={6}
                            xs={12}
                            name="surname2"
                            caption={t('generic.surname2')}
                            defVal={mship.surname2 || ''}
                            onChange={handleForm}
                        />

                        <SttFormSelect
                            grid
                            md={3}
                            sm={6}
                            xs={6}
                            name="identificationType"
                            caption={t('generic.identificationType')}
                            defVal={mship.identificationType || ''}
                            onChange={handleForm}
                            options={[
                                {
                                    caption: t('generic.dni'),
                                    value: 'DNI',
                                },
                                {
                                    caption: t('generic.passport'),
                                    value: 'PASSPORT',
                                },
                                {
                                    caption: t('generic.nie'),
                                    value: 'NIE',
                                },
                                {
                                    caption: t('generic.cif'),
                                    value: 'CIF',
                                },
                            ]}
                        />

                        <SttFormText
                            grid
                            md={3}
                            name="dni"
                            caption={t('generic.dni')}
                            defVal={mship.dni || ''}
                            onChange={handleForm}
                        />

                        <SttSelectGender
                            grid
                            md={3}
                            name="gender"
                            defVal={mship.gender}
                            onChange={handleForm}
                        />

                        {/* --- Birthday --- */}
                        <SttFormDate
                            grid
                            md={3}
                            name="birthday"
                            caption={t('generic.birthday')}
                            defVal={mship.birthday}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={8}
                            name="email"
                            caption={`${t('generic.email')} 1`}
                            defVal={mship.email || ''}
                            helperText={t('mship.email1Notice')}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={4}
                            name="mobile"
                            caption={`${t('generic.mobile')} 1`}
                            defVal={mship.mobile || ''}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={8}
                            name="email2"
                            caption={`${t('generic.email')} 2`}
                            defVal={mship.email2 || ''}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={4}
                            name="mobile2"
                            caption={`${t('generic.mobile')} 2`}
                            defVal={mship.mobile2 || ''}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            xs={12}
                            md={4}
                            name="address"
                            caption={t('generic.address')}
                            defVal={mship.address || ''}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={4}
                            name="postalCode"
                            caption={t('generic.postalCode')}
                            defVal={mship.postalCode || ''}
                            onChange={handleForm}
                        />

                        <SttSelectCity
                            grid
                            md={4}
                            name="city"
                            caption={t('generic.city')}
                            defVal={(mship.city && mship.city.name) || ''}
                            onChange={handleForm}
                            onSelect={handleForm}
                        />

                        <SttFormSelect
                            grid
                            md={4}
                            name="addrVia"
                            caption={t('generic.via')}
                            defVal={mship.addrVia || ''}
                            onChange={handleForm}
                            options={[
                                {
                                    caption: t('generic.street'),
                                    value: 'STREET',
                                },
                                {
                                    caption: t('generic.avenue'),
                                    value: 'AVENUE',
                                },
                                {
                                    caption: t('generic.square'),
                                    value: 'SQUARE',
                                },
                            ]}
                        />

                        <SttFormSelect
                            grid
                            md={4}
                            name="addrType"
                            caption={t('generic.buildingType')}
                            defVal={mship.addrType || ''}
                            onChange={handleForm}
                            options={[
                                {
                                    caption: t('generic.flat'),
                                    value: 'FLAT',
                                },
                                {
                                    caption: t('generic.house'),
                                    value: 'HOUSE',
                                },
                                {
                                    caption: t('generic.block'),
                                    value: 'BLOCK',
                                },
                            ]}
                        />

                        <SttFormText
                            grid
                            md={2}
                            name="addrNumber"
                            caption={t('generic.number')}
                            defVal={mship.addrNumber || ''}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={2}
                            name="addrLetter"
                            caption={t('generic.letter')}
                            defVal={mship.addrLetter || ''}
                            onChange={handleForm}
                        />

                        <SttFormTextarea
                            grid
                            md={12}
                            name="notes"
                            caption={t('generic.notes')}
                            defVal={mship.notes || ''}
                            onChange={handleForm}
                        />

                        <SttSelectUser
                            grid
                            xs={12}
                            md={7}
                            caption={t('generic.tutor')}
                            user={
                                mship.tutor
                                    ? ({
                                          ...mship.tutor,
                                          fullName:
                                              mship.tutor?.mship?.fullName ||
                                              mship?.tutor?.fullName ||
                                              ' ',
                                      } as any)
                                    : undefined
                            }
                            onSelect={(mshipSelected: MshipData | null) => {
                                handleForm({
                                    name: 'tutor',
                                    value: mshipSelected
                                        ? {
                                              ...mshipSelected?.user,
                                              mship: {
                                                  fullName: mshipSelected?.name,
                                              },
                                          }
                                        : null,
                                });
                            }}
                            returnMship
                        />

                        <SttFormText
                            grid
                            md={5}
                            name="iban"
                            caption="IBAN"
                            defVal={mship.iban || ''}
                            onChange={handleForm}
                            validation={[
                                {
                                    regex: /^$|^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}$/,
                                    error: t('mship.ibanEmptyOrValid'),
                                },
                            ]}
                            setValidationError={setValidateForm}
                        />
                    </Grid>

                    <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                        <SttButtonDelete
                            onClick={() => handleOnRemove()}
                            confirmation
                            permission={PERMISSIONS.CUSTOMER.DELETE}
                        />
                        <SttButtonSave
                            disabled={validateForm}
                            loading={updateMshipMutation.isPending}
                            onClick={() => handleOnSave()}
                            permission={PERMISSIONS.CUSTOMER.MODIFY}
                        />
                    </Box>
                </Grid>
            </Grid>

            <UploadFileDialog
                open={openUploadPhoto}
                types={['USER_PHOTO']}
                onClose={() => setOpenUploadPhoto(false)}
                uploadParams={{
                    idMship: mship && mship.id,
                }}
                onUploaded={(r: unknown) => {
                    const response = r as UploadResponse;
                    const { file } = response;
                    setOpenUploadPhoto(false);
                    setMship({
                        ...mship,
                        img: file,
                    });
                }}
            />

            <SttWebcamCaptureDialog
                mship={mship as any}
                open={openWebcamCapture}
                onClose={() => setOpenWebcamCapture(false)}
                onUploaded={(r: unknown) => {
                    const response = r as UploadResponse;
                    const { file } = response;
                    setOpenWebcamCapture(false);
                    setMship({
                        ...mship,
                        img: file,
                    });
                }}
            />

            {openedBoleto && (
                <BoletoDialog
                    item={openedBoleto}
                    onClose={() => setOpenedBoleto(null)}
                />
            )}
        </>
    );
}
